import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import AgoraRTC from "agora-rtc-sdk-ng"

// Use REACT_APP_AGORA_APP_ID from environment. Fallback to previous ID if not set.
// Note: in development you can set REACT_APP_MOCK_CALL=true to bypass backend.
const AGORA_APP_ID = process.env.REACT_APP_AGORA_APP_ID || "7574fd9fa9114a129d4ee1c38aaffcc4"
const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" })

const CallContext = createContext(null)

export const CallProvider = ({ children }) => {
  const [joined, setJoined] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [isVideoOff, setIsVideoOff] = useState(false)
  const [remoteUsers, setRemoteUsers] = useState([])
  const [remoteVideoOff, setRemoteVideoOff] = useState(false)
  const [remoteMicMuted, setRemoteMicMuted] = useState(false)
  const [showRemoteLeft, setShowRemoteLeft] = useState(false)
  const [callStarted, setCallStarted] = useState(false)
  const [callDuration, setCallDuration] = useState(0)
  const [isPiP, setIsPiP] = useState(false)
  const [callViewMode, setCallViewMode] = useState("original")
  const [pipPos, setPipPos] = useState({ x: window.innerWidth - 340, y: window.innerHeight - 260 })
  const [channelName, setChannelName] = useState("")
  const [userName, setUserName] = useState("")
  const [hasActiveCallView, setHasActiveCallView] = useState(false)
  const [pendingRestore, setPendingRestore] = useState(false)

  const isVideoCall = useRef(true)
  const localAudioTrackRef = useRef(null)
  const localVideoTrackRef = useRef(null)
  const joinedRef = useRef(false)
  const localVideoPlayedRef = useRef(false)

  // Always holds latest callViewMode — prevents stale closures
  const callViewModeRef = useRef("original")
  useEffect(() => {
    callViewModeRef.current = callViewMode
  }, [callViewMode])

  // Always holds latest remoteUsers — prevents stale closures
  const remoteUsersRef = useRef([])
  useEffect(() => {
    remoteUsersRef.current = remoteUsers
  }, [remoteUsers])

  useEffect(() => {
    joinedRef.current = joined
  }, [joined])

  useEffect(() => {
    const onResize = () => {
      setPipPos(prev => ({
        x: Math.min(prev.x, Math.max(0, window.innerWidth - 320)),
        y: Math.min(prev.y, Math.max(0, window.innerHeight - 240)),
      }))
    }
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  useEffect(() => {
    if (callViewMode === "minimized" && !isPiP) setIsPiP(true)
    else if (callViewMode !== "minimized" && isPiP) setIsPiP(false)
  }, [callViewMode, isPiP])

  useEffect(() => {
    let timer = null
    if (callStarted) {
      timer = setInterval(() => setCallDuration(prev => prev + 1), 1000)
    }
    return () => { if (timer) clearInterval(timer) }
  }, [callStarted])

  // Reads from ref so it never goes stale
  const getContainerId = useCallback((base, uid = "") => {
    const mode = callViewModeRef.current
    if (mode === "minimized") return `pip-${base}${uid ? `-${uid}` : ""}`
    if (mode === "fullscreen") return `fullscreen-${base}${uid ? `-${uid}` : ""}`
    return `${base}${uid ? `-${uid}` : ""}`
  }, [])

  // Centralized replay — reads refs, clears containers, checks dimensions
  const replayTracks = useCallback(() => {
    const mode = callViewModeRef.current
    const currentRemoteUsers = remoteUsersRef.current

    console.log("[replayTracks] mode:", mode)
    console.log("[replayTracks] joined:", joinedRef.current)
    console.log("[replayTracks] isVideoCall:", isVideoCall.current)
    console.log("[replayTracks] localVideoTrack exists:", !!localVideoTrackRef.current)
    console.log("[replayTracks] remoteUsers count:", currentRemoteUsers.length)

    if (!joinedRef.current || !isVideoCall.current) return

    // Local video
    const localContainerId = (() => {
      if (mode === "minimized") return "pip-local-video"
      if (mode === "fullscreen") return "fullscreen-local-video"
      return "local-video"
    })()

    console.log("[replayTracks] local container id:", localContainerId)
    const localTarget = document.getElementById(localContainerId)
    console.log("[replayTracks] local container exists:", !!localTarget)

    if (localVideoTrackRef.current && localTarget) {
      const { width, height } = localTarget.getBoundingClientRect()
      console.log("[replayTracks] local container dimensions:", width, height)
      if (width > 0 && height > 0) {
        try {
          console.log("[replayTracks] stopping local track")
          localVideoTrackRef.current.stop()
          console.log("[replayTracks] clearing local container")
          localTarget.innerHTML = ""
          console.log("[replayTracks] playing local into:", localContainerId)
          localVideoTrackRef.current.play(localContainerId)
        } catch (err) {
          console.error("[replayTracks] local play error:", err)
        }
      } else {
        console.warn("[replayTracks] local container has zero dimensions, skipping")
      }
    }

    // Remote videos
    currentRemoteUsers.forEach(user => {
      const remoteContainerId = (() => {
        if (mode === "minimized") return `pip-remote-video-${user.uid}`
        if (mode === "fullscreen") return `fullscreen-remote-video-${user.uid}`
        return `remote-video-${user.uid}`
      })()

      console.log("[replayTracks] remote container id:", remoteContainerId)
      const remoteTarget = document.getElementById(remoteContainerId)
      console.log("[replayTracks] remote exists:", !!remoteTarget, "track:", !!user.videoTrack)

      if (user.videoTrack && remoteTarget) {
        try {
          user.videoTrack.stop()
          remoteTarget.innerHTML = ""
          user.videoTrack.play(remoteContainerId)
          console.log("[replayTracks] played remote into:", remoteContainerId)
        } catch (err) {
          console.error("[replayTracks] remote play error:", err)
        }
      }
    })
  }, [])

  // Retry loop — waits for DOM container to exist and have dimensions
  const waitAndReplay = useCallback(() => {
  const mode = callViewModeRef.current

  const localContainerId = (() => {
    if (mode === "minimized") return "pip-local-video"
    if (mode === "fullscreen") return "fullscreen-local-video"
    return "local-video"
  })()

  let attempts = 0
  const maxAttempts = 40

  const interval = setInterval(() => {
    attempts++
    const el = document.getElementById(localContainerId)

    if (el) {
      const { width, height } = el.getBoundingClientRect()
      if (width > 0 && height > 0) {
        clearInterval(interval)
        replayTracks()
        return
      }
    }

    if (attempts >= maxAttempts) {
      clearInterval(interval)
      console.warn("[waitAndReplay] fullscreen container never became visible", { localContainerId, attempts })
    }
  }, 220)

  return () => clearInterval(interval)
}, [replayTracks])

  // Trigger waitAndReplay on mode or remote user changes
  useEffect(() => {
    if (!joined || !isVideoCall.current) return

    let cleanup = null
    const timeout = window.setTimeout(() => {
      cleanup = waitAndReplay()
    }, 180)

    return () => {
      window.clearTimeout(timeout)
      if (typeof cleanup === "function") cleanup()
    }
  }, [callViewMode, joined, remoteUsers, waitAndReplay])

  // handleUserPublished reads mode from ref to avoid stale closure
  const handleUserPublished = useCallback(async (user, mediaType) => {
    await client.subscribe(user, mediaType)

    setRemoteUsers(prev => (prev.some(u => u.uid === user.uid) ? prev : [...prev, user]))

    if (mediaType === "video") {
      setRemoteVideoOff(false)
      setTimeout(() => {
        const mode = callViewModeRef.current
        const remoteContainerId = (() => {
          if (mode === "minimized") return `pip-remote-video-${user.uid}`
          if (mode === "fullscreen") return `fullscreen-remote-video-${user.uid}`
          return `remote-video-${user.uid}`
        })()
        console.log("[handleUserPublished] playing remote into:", remoteContainerId)
        const remoteTarget = document.getElementById(remoteContainerId)
        if (remoteTarget) {
          remoteTarget.innerHTML = ""
          user.videoTrack?.play(remoteContainerId)
        } else {
          console.warn("[handleUserPublished] remote container not found:", remoteContainerId)
        }
      }, 150)
    }

    if (mediaType === "audio") {
      setRemoteMicMuted(false)
      user.audioTrack?.play()
    }

    setCallStarted(true)
  }, [])

  const handleUserUnpublished = useCallback((user, mediaType) => {
    if (mediaType === "video") setRemoteVideoOff(true)
    if (mediaType === "audio") setRemoteMicMuted(true)
  }, [])

  const handleUserLeft = useCallback((user) => {
    setRemoteUsers(prev => {
      const updated = prev.filter(u => u.uid !== user.uid)
      if (updated.length === 0) {
        setCallStarted(false)
        setCallDuration(0)
      }
      return updated
    })
    setShowRemoteLeft(true)
    window.setTimeout(() => setShowRemoteLeft(false), 3000)
  }, [])

  const handleUserJoined = useCallback(() => {
    setCallStarted(true)
  }, [])

  useEffect(() => {
    client.on("user-published", handleUserPublished)
    client.on("user-unpublished", handleUserUnpublished)
    client.on("user-left", handleUserLeft)
    client.on("user-joined", handleUserJoined)
    return () => {
      client.off("user-published", handleUserPublished)
      client.off("user-unpublished", handleUserUnpublished)
      client.off("user-left", handleUserLeft)
      client.off("user-joined", handleUserJoined)
    }
  }, [handleUserPublished, handleUserUnpublished, handleUserLeft, handleUserJoined])

  const joinCall = useCallback(async ({ channel, visitType = "video", user = "Vet" } = {}) => {
    if (connecting || joined) return

    setConnecting(true)
    setChannelName(channel || channelName)
    setUserName(user)
    isVideoCall.current = visitType !== "audio"

    try {
      try { await client.leave() } catch (e) {}
      await new Promise(resolve => setTimeout(resolve, 300))

      const mockEnabled = String(process.env.REACT_APP_MOCK_CALL).toLowerCase() === 'true'
      let data

      if (mockEnabled) {
        // Mock data for local/dev testing. token=null works only when App Certificate is disabled.
        data = {
          channelName: channel || channelName || "test-channel",
          token: null,
          uid: 12345,
          callType: visitType,
        }
        console.info('[CallContext] REACT_APP_MOCK_CALL enabled, using mock call data', data)
      } else {
        const auth = JSON.parse(localStorage.getItem("authUser") || "{}")
        const jwtToken = auth?.token || ""

        const res = await fetch(`${process.env.REACT_APP_BASE_URL}api/vet/v1/getCallToken`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${jwtToken}`,
          },
          body: JSON.stringify({ appointmentId: channel || channelName }),
        })

        if (!res.ok) {
          const text = await res.text().catch(() => "")
          throw new Error(`getCallToken failed: ${res.status} ${text}`)
        }

        data = await res.json()
      }

      const isVideo = String(data.callType || visitType).toLowerCase() !== "audio"
      isVideoCall.current = isVideo

      // Note: token may be null in mock/dev when Agora App Certificate is disabled.
      await client.join(AGORA_APP_ID, data.channelName, data.token, data.uid)

      if (!isVideo) {
        const audioTrack = await AgoraRTC.createMicrophoneAudioTrack()
        localAudioTrackRef.current = audioTrack
        await client.publish([audioTrack])
      } else {
        const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks()
        localAudioTrackRef.current = audioTrack
        localVideoTrackRef.current = videoTrack
        await client.publish([audioTrack, videoTrack])
      }

      setJoined(true)
      setCallDuration(0)
      setIsMuted(false)
      setIsVideoOff(false)
      setIsPiP(false)
      setCallViewMode("original")
      localVideoPlayedRef.current = false
    } catch (err) {
      console.error(err)
      localAudioTrackRef.current?.stop()
      localAudioTrackRef.current?.close()
      localVideoTrackRef.current?.stop()
      localVideoTrackRef.current?.close()
      localAudioTrackRef.current = null
      localVideoTrackRef.current = null

      if (
        err.code === "PERMISSION_DENIED" ||
        err.message?.includes("Permission denied") ||
        err.message?.includes("NotAllowedError")
      ) {
        alert("Camera/Microphone access was denied. Please allow access in your browser settings and try again.")
      } else if (err.message?.includes("NotFoundError")) {
        alert("No camera or microphone found on this device.")
      } else {
        alert("Failed to join the call. Please try again.")
      }
    } finally {
      setConnecting(false)
    }
  }, [channelName, connecting, joined])

  const leaveCall = useCallback(async () => {
    try {
      localAudioTrackRef.current?.stop()
      localAudioTrackRef.current?.close()
      localVideoTrackRef.current?.stop()
      localVideoTrackRef.current?.close()
      localAudioTrackRef.current = null
      localVideoTrackRef.current = null
      if (client.connectionState !== "DISCONNECTED") await client.leave()
    } catch (err) {
      console.error("Leave call failed:", err)
    }

    localVideoPlayedRef.current = false
    setJoined(false)
    setCallStarted(false)
    setCallDuration(0)
    setRemoteUsers([])
    setIsMuted(false)
    setIsVideoOff(false)
    setIsPiP(false)
    setCallViewMode("original")
    setRemoteVideoOff(false)
    setRemoteMicMuted(false)
    setShowRemoteLeft(false)
  }, [])

  useEffect(() => {
    return () => {
      if (joinedRef.current) leaveCall()
    }
  }, [leaveCall])

  const toggleMic = useCallback(async () => {
    if (!localAudioTrackRef.current) return
    await localAudioTrackRef.current.setMuted(!isMuted)
    setIsMuted(prev => !prev)
  }, [isMuted])

  const toggleVideo = useCallback(async () => {
    if (!localVideoTrackRef.current) return
    await localVideoTrackRef.current.setMuted(!isVideoOff)
    setIsVideoOff(prev => !prev)
  }, [isVideoOff])

  const isAudioCall = !isVideoCall.current

  return (
    <CallContext.Provider
      value={{
        joined,
        connecting,
        isMuted,
        isVideoOff,
        remoteUsers,
        remoteVideoOff,
        remoteMicMuted,
        showRemoteLeft,
        callStarted,
        callDuration,
        callViewMode,
        isPiP,
        pipPos,
        channelName,
        userName,
        hasActiveCallView,
        pendingRestore,
        setHasActiveCallView,
        isVideoCall: isVideoCall.current,
        localVideoTrackRef,
        localAudioTrackRef,
        isAudioCall,
        setChannelName,
        setUserName,
        setIsPiP,
        setPipPos,
        setCallViewMode,
        setPendingRestore,
        joinCall,
        leaveCall,
        toggleMic,
        toggleVideo,
        replayTracks,
        waitAndReplay,   // ← was missing, now exported
      }}
    >
      {children}
    </CallContext.Provider>
  )
}

export const useCall = () => {
  const context = useContext(CallContext)
  if (!context) throw new Error("useCall must be used within a CallProvider")
  return context
}
